import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [showNewsletter, setShowNewsletter] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you for contacting us! We will get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Thank you for subscribing!');
    setShowNewsletter(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="https://public.readdy.ai/ai/img_res/48d2f9ca-b28e-4918-a229-8105eb53a83d.png" 
                alt="IN TIME Logo" 
                className="h-10 w-auto"
              />
            </div>
            <div className="hidden md:flex items-center space-x-10">
              <a href="#home" className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'
              }`}>Home</a>
              <a href="#about" className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'
              }`}>About</a>
              <a href="#vpn-service" className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'
              }`}>VPN Service</a>
              <a href="#features" className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'
              }`}>Features</a>
              <a href="#support" className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'
              }`}>Support</a>
              <a href="#contact" className={`text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white hover:text-cyan-300'
              }`}>Contact</a>
            </div>
            <a 
              href="#contact" 
              className="hidden md:block px-8 py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-full font-medium hover:from-cyan-600 hover:to-cyan-700 transition-all cursor-pointer whitespace-nowrap"
            >
              Get Started
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-900">
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(6, 182, 212, 0.3) 0%, transparent 50%),
                             radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
                             radial-gradient(circle at 40% 20%, rgba(14, 165, 233, 0.2) 0%, transparent 50%)`
          }}></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-2xl">
            <h1 className="text-7xl font-bold text-white leading-tight mb-6">
              Secure Your<br />
              Digital Life<br />
              With VPN
            </h1>
            <p className="text-xl text-gray-200 mb-12 font-light">
              Enterprise-grade encryption for your privacy
            </p>
            <a 
              href="#vpn-service" 
              className="inline-flex items-center px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full text-lg font-medium hover:from-cyan-600 hover:to-blue-600 transition-all cursor-pointer whitespace-nowrap"
            >
              Start Now
              <div className="ml-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-arrow-right-line text-white"></i>
              </div>
            </a>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 pb-16 px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-9xl font-bold text-white opacity-90">IN TIME</h2>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-20">
            <div className="w-full md:w-1/2">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/4ad5ce81f6ba9795242d4c4d478f71dd.jpg"
                  alt="Global VPN Network"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            
            <div className="w-full md:w-1/2">
              <div className="inline-block px-4 py-2 bg-cyan-100 text-cyan-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-8">
                About IN TIME
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-10 leading-tight">
                Protecting Your<br />
                Privacy Since Day One
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At IN TIME d.o.o., we are dedicated to providing cutting-edge VPN solutions that safeguard your online privacy and security. Our mission is to ensure that everyone can browse the internet freely and securely, without compromising their personal data or digital footprint.
              </p>
              <p className="text-lg text-gray-600 mb-12 leading-relaxed">
                With advanced encryption technology and a commitment to transparency, we deliver reliable VPN services that protect your digital identity across all your devices and online activities.
              </p>
              
              <div className="border-l-4 border-cyan-500 pl-6 space-y-5 bg-white p-8 rounded-lg shadow-sm">
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line text-cyan-500 text-xl"></i>
                  <a href="mailto:support@intimedoo.com" className="text-base text-cyan-600 hover:text-cyan-700 cursor-pointer">
                    support@intimedoo.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-cyan-500 text-xl"></i>
                  <a href="tel:+18444937398" className="text-base text-cyan-600 hover:text-cyan-700 cursor-pointer">
                    +1 844-493-7398
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-cyan-500 text-xl mt-1"></i>
                  <span className="text-base text-gray-700">
                    Jurckova cesta 234, Ljubljana 1000, Slovenia
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Service Section */}
      <section id="vpn-service" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
              <span className="text-sm text-gray-500 uppercase tracking-wider font-semibold">Our Service</span>
            </div>
            <h2 className="text-6xl font-bold text-gray-900 mb-5 leading-tight">
              Military-Grade VPN<br />
              Protection
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Experience true internet freedom with our advanced VPN technology
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-12 text-white">
              <h3 className="text-3xl font-bold mb-4">Global Network</h3>
              <p className="text-gray-300 mb-8 leading-relaxed">
                Connect to servers worldwide for unrestricted access to content and services from anywhere on the planet.
              </p>
              <div className="flex items-center justify-center my-12">
                <img 
                  src="https://readdy.ai/api/search-image?query=minimalist%203D%20illustration%20of%20a%20glowing%20globe%20with%20network%20connection%20lines%20spreading%20across%20continents%2C%20digital%20world%20map%20with%20cyan%20and%20blue%20light%20trails%2C%20modern%20tech%20icon%20on%20dark%20background%2C%20clean%20geometric%20design&width=300&height=300&seq=service1&orientation=squarish"
                  alt="Global Network"
                  className="w-48 h-48 object-contain"
                />
              </div>
              <div className="w-16 h-0.5 bg-cyan-500"></div>
            </div>

            <div className="bg-gray-50 rounded-3xl p-12">
              <div className="inline-block px-3 py-1 bg-cyan-500 text-white rounded-full text-xs font-semibold uppercase mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  Active
                </div>
              </div>
              <div className="flex items-center justify-center my-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=3D%20shield%20icon%20with%20checkmark%20symbol%20in%20gradient%20cyan%20to%20blue%20colors%2C%20modern%20security%20badge%20illustration%2C%20glossy%20metallic%20finish%20with%20soft%20glow%20effect%2C%20clean%20minimalist%20design%20on%20light%20background&width=280&height=280&seq=service2&orientation=squarish"
                  alt="Bank-Level Encryption"
                  className="w-40 h-40 object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Bank-Level Encryption</h3>
              <p className="text-gray-600 leading-relaxed">
                AES-256 encryption protects your data with the same security standards used by financial institutions worldwide.
              </p>
            </div>

            <div className="bg-gray-50 rounded-3xl p-12">
              <div className="flex items-center justify-center my-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=3D%20speed%20gauge%20or%20speedometer%20icon%20in%20gradient%20purple%20to%20pink%20colors%2C%20modern%20performance%20meter%20illustration%20with%20glowing%20needle%2C%20sleek%20metallic%20design%20with%20soft%20lighting%20on%20light%20background&width=280&height=280&seq=service3&orientation=squarish"
                  alt="Lightning Fast"
                  className="w-40 h-40 object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Lightning Fast</h3>
              <p className="text-gray-600 leading-relaxed">
                Optimized servers for maximum speed ensure seamless browsing, streaming, and downloading without slowdowns.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-start justify-between mb-24 gap-12">
            <h2 className="text-6xl font-bold text-gray-900 leading-tight md:w-2/5">
              Why Choose<br />
              IN TIME VPN
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed md:w-1/2">
              Our VPN service combines cutting-edge security features with user-friendly design to deliver the ultimate privacy protection. We prioritize your safety without compromising on performance or ease of use.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-8">
            <div className="md:col-span-5 bg-gray-900 rounded-2xl p-10 relative overflow-hidden">
              <h3 className="text-3xl font-bold text-white mb-4">No-Log Policy</h3>
              <p className="text-gray-300 mb-8 text-lg font-light">
                Your activity stays private, always. We never track, collect, or store your browsing data.
              </p>
              <div className="mt-12">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract%203D%20visualization%20of%20encrypted%20data%20flow%20with%20glowing%20cyan%20and%20white%20particles%20streaming%20through%20digital%20space%2C%20futuristic%20privacy%20concept%20with%20light%20trails%20and%20geometric%20patterns%20on%20dark%20background&width=500&height=300&seq=feature1&orientation=landscape"
                  alt="No-Log Policy"
                  className="w-full h-48 object-cover rounded-lg opacity-80"
                />
              </div>
            </div>

            <div className="md:col-span-3 bg-white rounded-2xl p-8 text-center">
              <div className="inline-block px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-xs font-semibold uppercase mb-6">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line"></i>
                  Secure
                </div>
              </div>
              <div className="flex items-center justify-center my-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=3D%20lock%20icon%20in%20gradient%20blue%20colors%20with%20glowing%20effect%2C%20modern%20security%20symbol%20illustration%2C%20metallic%20finish%20with%20soft%20shadows%20on%20light%20background%2C%20minimalist%20clean%20design&width=200&height=200&seq=feature2&orientation=squarish"
                  alt="Kill Switch"
                  className="w-32 h-32 object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Kill Switch</h3>
              <p className="text-gray-600 text-sm">
                Automatic protection if connection drops
              </p>
            </div>

            <div className="md:col-span-4 bg-white rounded-2xl p-8 text-center">
              <div className="flex items-center justify-center my-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=3D%20illustration%20of%20multiple%20connected%20devices%20including%20smartphone%20tablet%20laptop%20and%20desktop%20computer%20in%20modern%20gradient%20blue%20colors%2C%20multi-device%20connectivity%20concept%20with%20glowing%20links%20on%20light%20background&width=250&height=200&seq=feature3&orientation=landscape"
                  alt="Multi-Device"
                  className="w-48 h-32 object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Multi-Device</h3>
              <p className="text-gray-600 text-sm">
                5 simultaneous connections across all your devices
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-line text-cyan-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">DNS Leak Protection</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Prevents DNS requests from leaking outside the VPN tunnel, ensuring complete privacy protection.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-git-branch-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Split Tunneling</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Choose which apps use the VPN and which connect directly for optimal performance and flexibility.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-customer-service-2-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">24/7 Support</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Our dedicated support team is always available to help you with any questions or technical issues.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-cyan-100 text-cyan-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
              Support Center
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">We're Here to Help</h2>
            <p className="text-lg text-gray-600">
              Get assistance whenever you need it
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
            <div className="bg-blue-50 rounded-2xl p-14">
              <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-mail-line text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Email Support</h3>
              <p className="text-base text-gray-600 mb-6">
                Get detailed help via email
              </p>
              <a 
                href="mailto:support@intimedoo.com" 
                className="text-lg text-cyan-600 hover:text-cyan-700 font-medium cursor-pointer block mb-4"
              >
                support@intimedoo.com
              </a>
              <p className="text-sm text-gray-500">
                Response within 24 hours
              </p>
            </div>

            <div className="bg-green-50 rounded-2xl p-14">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-phone-line text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Phone Support</h3>
              <p className="text-base text-gray-600 mb-6">
                Speak directly with our team
              </p>
              <a 
                href="tel:+18444937398" 
                className="text-lg text-green-600 hover:text-green-700 font-medium cursor-pointer block mb-4"
              >
                +1 844-493-7398
              </a>
              <p className="text-sm text-gray-500">
                Available 24/7
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
            <div>
              <div className="flex items-center gap-2 mb-8">
                <div className="w-2 h-2 bg-white rounded-full"></div>
                <span className="text-sm text-gray-400 uppercase tracking-wider font-semibold">Contact Us</span>
              </div>
              <h2 className="text-6xl font-bold text-white mb-8 leading-tight">
                Get in Touch
              </h2>
              <p className="text-lg text-gray-300 mb-16 leading-relaxed font-light">
                Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <i className="ri-map-pin-line text-cyan-400 text-2xl mt-1"></i>
                  <span className="text-base text-white">
                    Jurckova cesta 234, Ljubljana 1000, Slovenia
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <i className="ri-phone-line text-cyan-400 text-2xl"></i>
                  <a href="tel:+18444937398" className="text-base text-white hover:text-cyan-400 cursor-pointer">
                    +1 844-493-7398
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <i className="ri-mail-line text-cyan-400 text-2xl"></i>
                  <a href="mailto:support@intimedoo.com" className="text-base text-white hover:text-cyan-400 cursor-pointer">
                    support@intimedoo.com
                  </a>
                </div>
              </div>
            </div>

            <div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="w-full bg-transparent border-b-2 border-gray-600 text-white placeholder-gray-400 py-4 focus:border-cyan-500 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      placeholder="Your Email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="w-full bg-transparent border-b-2 border-gray-600 text-white placeholder-gray-400 py-4 focus:border-cyan-500 focus:outline-none transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    required
                    className="w-full bg-transparent border-b-2 border-gray-600 text-white placeholder-gray-400 py-4 focus:border-cyan-500 focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <textarea
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                    rows={5}
                    className="w-full bg-transparent border-b-2 border-gray-600 text-white placeholder-gray-400 py-4 focus:border-cyan-500 focus:outline-none transition-colors resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-cyan-500 text-white py-4 rounded-lg font-medium hover:bg-cyan-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white pt-20 pb-8 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div>
              <img 
                src="https://public.readdy.ai/ai/img_res/48d2f9ca-b28e-4918-a229-8105eb53a83d.png" 
                alt="IN TIME Logo" 
                className="h-8 w-auto mb-6"
              />
              <p className="text-sm text-gray-400 leading-relaxed">
                Your trusted partner for online privacy and security solutions. Protecting your digital life with advanced VPN technology.
              </p>
            </div>

            <div>
              <h4 className="text-xs text-gray-400 uppercase tracking-widest mb-6 font-semibold">Newsletter</h4>
              <div className="relative">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full bg-transparent border-b border-gray-700 text-white placeholder-gray-500 py-3 pr-10 focus:border-cyan-500 focus:outline-none transition-colors text-sm"
                  onFocus={() => setShowNewsletter(true)}
                />
                <button 
                  onClick={() => setShowNewsletter(true)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-cyan-400 hover:text-cyan-300 cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Subscribe to our updates. <a href="#" className="text-cyan-400 hover:text-cyan-300 cursor-pointer">Privacy Policy</a>
              </p>
            </div>

            <div>
              <h4 className="text-xs text-gray-400 uppercase tracking-widest mb-6 font-semibold">Quick Links</h4>
              <ul className="space-y-4">
                <li><a href="#home" className="text-sm text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">Home</a></li>
                <li><a href="#about" className="text-sm text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">About</a></li>
                <li><a href="#vpn-service" className="text-sm text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">VPN Service</a></li>
                <li><a href="#features" className="text-sm text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">Features</a></li>
                <li><a href="#support" className="text-sm text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">Support</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs text-gray-400 uppercase tracking-widest mb-6 font-semibold">Connect</h4>
              <ul className="space-y-4">
                <li><a href="#" className="text-sm text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">Twitter</a></li>
                <li><a href="#" className="text-sm text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">LinkedIn</a></li>
                <li><a href="#" className="text-sm text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">Facebook</a></li>
              </ul>
            </div>
          </div>

          <div className="relative py-16 mb-8">
            <h2 className="text-9xl font-bold text-amber-100/10 tracking-tight">IN TIME</h2>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              © 2025 IN TIME d.o.o. All rights reserved
            </p>
            <a 
              href="https://readdy.ai/?origin=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-sm text-gray-500 hover:text-cyan-400 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>

      {/* Newsletter Popup */}
      {showNewsletter && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowNewsletter(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Subscribe to Newsletter</h3>
              <button onClick={() => setShowNewsletter(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                placeholder="Enter your email"
                required
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 mb-4 focus:border-cyan-500 focus:outline-none"
              />
              <button
                type="submit"
                className="w-full bg-cyan-500 text-white py-3 rounded-lg font-medium hover:bg-cyan-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}